import React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DateRangeFilter.module.scss";
import { enGB } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Calendar as LucideCalendar, ChevronLeft, ChevronRight } from "lucide-react";

// Register the locale that starts weeks on Monday
registerLocale("en-GB", enGB);

const DateRangeFilter = ({
  dateRange,
  handleDateRangeChange,
  isClearable,
  className = "",
}) => {
  const { t } = useTranslation();

  const [startDate, endDate] = dateRange;

  const CustomButton = React.forwardRef(({ value, onClick }, ref) => (
    <button
      type="button"
      className={`${styles.customInput} ${!startDate && !endDate ? styles.placeholder : ""} ${className}`}
      onClick={onClick}
      ref={ref}
    >
      <span className={styles.label}>
        {value ? value : t("dateRangeFilter.placeholder")}
      </span>
      <LucideCalendar className={styles.calendarIcon} size={16} />
    </button>
  ));
  CustomButton.displayName = "DateRangeFilterButton";

  const renderHeader = (label) => ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className={styles.header}>
      <span className={styles.sectionLabel}>{label}</span>
      <div className={styles.monthNav}>
        <button
          type="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className={styles.navButton}
          aria-label={t("dateRangeFilter.previousMonth", "Previous month")}
        >
          <ChevronLeft size={16} />
        </button>
        <span className={styles.monthLabel}>
          {format(date, "MMMM yyyy", { locale: enGB })}
        </span>
        <button
          type="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className={styles.navButton}
          aria-label={t("dateRangeFilter.nextMonth", "Next month")}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );

  const handleSelect = (date, target) => {
    if (!date) {
      handleDateRangeChange(target === "start" ? [null, endDate] : [startDate, null]);
      return;
    }

    if (target === "start") {
      const newStart = date;
      const newEnd = endDate && endDate < newStart ? newStart : endDate;
      handleDateRangeChange([newStart, newEnd]);
    } else {
      handleDateRangeChange([startDate, date]);
    }
  };

  const CalendarContainer = React.forwardRef(({ className, children }, ref) => (
    <div ref={ref} className={`${className || ""} ${styles.rangeContainer}`}>
      <div className={styles.calendarGrid}>
        <div className={styles.calendarSection}>
          <DatePicker
            inline
            selectsStart
            startDate={startDate}
            endDate={endDate}
            selected={startDate}
            onChange={(date) => handleSelect(date, "start")}
            dateFormat="dd/MM/yyyy"
            locale="en-GB"
            renderCustomHeader={renderHeader(t("dateRangeFilter.startDate", "Start Date"))}
            calendarClassName={styles.rangeCalendar}
            openToDate={startDate || endDate || new Date()}
            enableTabLoop={false}
          />
        </div>
        <div className={styles.calendarSection}>
          <DatePicker
            inline
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            selected={endDate}
            onChange={(date) => handleSelect(date, "end")}
            dateFormat="dd/MM/yyyy"
            locale="en-GB"
            renderCustomHeader={renderHeader(t("dateRangeFilter.endDate", "End Date"))}
            calendarClassName={styles.rangeCalendar}
            minDate={startDate || undefined}
            openToDate={endDate || startDate || new Date()}
            enableTabLoop={false}
          />
        </div>
      </div>
      {isClearable && (
        <button
          type="button"
          onClick={() => handleDateRangeChange([null, null])}
          className={styles.clearButton}
          disabled={!startDate && !endDate}
        >
          {t("dateRangeFilter.clearDates", "Clear Dates")}
        </button>
      )}
    </div>
  ));
  CalendarContainer.displayName = "DateRangeCalendarContainer";

  return (
    <DatePicker
      selectsRange
      startDate={startDate}
      endDate={endDate}
      onChange={(update) => handleDateRangeChange(update)}
      dateFormat="dd/MM/yyyy"
      wrapperClassName={`${styles.dateRangeWrapper} ${className}`.trim()}
      shouldCloseOnSelect={false}
      calendarContainer={CalendarContainer}
      popperClassName={styles.rangePopper}
      customInput={<CustomButton />}
      locale="en-GB"
      popperPlacement="bottom-start"
      enableTabLoop={false}
      popperProps={{
        // Use fixed positioning so the popper doesn't affect document scrollbars/layout.
        strategy: "fixed",
        modifiers: [
          {
            name: "flip",
            options: {
              fallbackPlacements: [],
            },
          },
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 0,
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 10],
            },
          },
          {
            // Keep popper width in sync with the trigger
            name: "sameWidth",
            enabled: true,
            phase: "beforeWrite",
            requires: ["computeStyles"],
            fn: ({ state }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            effect: ({ state }) => {
              state.elements.popper.style.width = `${state.elements.reference.getBoundingClientRect().width}px`;
            },
          },
        ],
      }}
    />
  );
};

export default DateRangeFilter;
